import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Table, Spinner, Button, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast } from "react-toastify";
import Pagination from "../../Components/Paginate";
import { FaMoneyBillWave, FaEye, FaDownload, FaFilter, FaCalendarAlt, FaCheckCircle, FaEllipsisV, FaCheck } from "react-icons/fa";
import { fetchSettlements, processPayout } from "../../redux/actions/SettlementActions";
import axios from "axios";

const SettlementManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    SettlementReducer: { settlements, count: settlementsCount, loading },
    AuthReducer: { auth }
  } = useSelector((state) => state);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [paginate, setPaginate] = useState(10);
  const [status, setStatus] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [actionDropdowns, setActionDropdowns] = useState({});
  const [payoutModal, setPayoutModal] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);

  useEffect(() => {
    const params = {
      page: page + 1,
      limit: paginate,
      search,
      status: status !== 'all' ? status : undefined,
      startDate: customStartDate || undefined,
      endDate: customEndDate || undefined
    };
    dispatch(fetchSettlements(params));
  }, [dispatch, page, search, paginate, status, dateRange, customStartDate, customEndDate]);

  const handlePayout = (settlementId) => {
    dispatch(processPayout(settlementId));
    setPayoutModal(false);
  };

  const handleGenerateReport = async () => {
    try {
      const response = await axios.get("/api/admin/v1/settlement-report", {
        headers: { Authorization: `Bearer ${auth?.token}` },
        params: {
          status: status !== 'all' ? status : undefined,
          startDate: customStartDate || undefined,
          endDate: customEndDate || undefined
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `settlement-report-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to generate report");
    }
  };

  const handleExportCSV = () => {
    if (!settlements?.length) return;
    const csvData = settlements.map((settlement, index) => ({
      "SL. No.": index + 1,
      "Settlement ID": settlement.settlement_id || settlement.id,
      "Partner Name": settlement.partner_name,
      "Period": `${new Date(settlement.period_start).toLocaleDateString()} - ${new Date(settlement.period_end).toLocaleDateString()}`,
      "Total Amount": `Rs.${settlement.net_payable || settlement.total_amount}`,
      "Status": settlement.status,
      "Payment Mode": settlement.payment_mode || 'Not Set',
      "Payout Date": settlement.payout_date ? new Date(settlement.payout_date).toLocaleDateString() : 'N/A'
    }));
    
    const csv = [Object.keys(csvData[0]).join(','), ...csvData.map(row => Object.values(row).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'settlements.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setDateRange("all");
    setShowCustomRange(false);
    setCustomStartDate("");
    setCustomEndDate("");
    setPage(0);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'warning',
      processing: 'info',
      completed: 'success',
      failed: 'danger'
    };
    return <Badge color={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 text-white fs-4">
              <FaMoneyBillWave className="me-2" />
              Settlement Management
            </h5>
            <div className="d-flex gap-2">
              <Button color="primary" onClick={() => navigate("/settlements/create")}>
                <FaMoneyBillWave className="me-1" />
                Create Settlement
              </Button>
              <Button color="outline-primary" onClick={handleGenerateReport}>
                Generate Report
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="bg-white px-3 py-4 rounded-2 my-4"
        style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="flex-grow-1" style={{ maxWidth: "190px" }}>
            <Input
              type="text"
              className="form-control"
              placeholder="🔍 Search by ID, Partner..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="d-flex flex-wrap gap-2">
            <div className="position-relative">
              <FaCheckCircle className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </Input>
            </div>
            <div className="position-relative">
              <FaCalendarAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setShowCustomRange(e.target.value === "custom");
                }}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </Input>
            </div>
            <Dropdown
              isOpen={exportDropdownOpen}
              toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
            >
              <DropdownToggle className="btn btn-outline-secondary text-white">
                <FaDownload className="me-1" />
                Export
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={handleExportCSV}>
                  Export CSV
                </DropdownItem>
                <DropdownItem onClick={handleGenerateReport}>
                  Export PDF Report
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <button
              className="btn btn-outline-danger"
              onClick={clearFilters}
            >
              <FaFilter className="me-1" />
              Clear Filters
            </button>
          </div>
        </div>
        
        {showCustomRange && (
          <div className="d-flex align-items-center gap-2 mb-3 p-3 bg-light rounded">
            <label className="fw-bold mb-0">From:</label>
            <Input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              style={{ width: "150px" }}
            />
            <label className="fw-bold mb-0">To:</label>
            <Input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              style={{ width: "150px" }}
            />
          </div>
        )}

        <div className="row">
          <div className="col-md-12">
            <Table hover responsive>
              <thead>
                <tr>
                  <th>SL. No.</th>
                  <th>Settlement ID</th>
                  <th>Partner Name</th>
                  <th>Period</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Payment Mode</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <Spinner />
                    </td>
                  </tr>
                ) : settlements.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">No settlements found</td>
                  </tr>
                ) : (
                  settlements.map((settlement, index) => (
                    <tr key={settlement.id}>
                      <th scope="row">{index + 1 + page * paginate}</th>
                      <td>{settlement.settlement_id || settlement.id}</td>
                      <td>{settlement.partner_name}</td>
                      <td>
                        {new Date(settlement.period_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} - {new Date(settlement.period_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td>₹{parseFloat(settlement.net_payable || settlement.total_amount).toLocaleString()}</td>
                      <td>{getStatusBadge(settlement.status.toLowerCase())}</td>
                      <td>
                        <span className={`badge ${settlement.payment_mode ? 'bg-info' : 'bg-secondary'}`}>
                          {settlement.payment_mode || 'Not Set'}
                        </span>
                      </td>
                      <td>
                        <Dropdown
                          isOpen={actionDropdowns[settlement.id] || false}
                          toggle={() => setActionDropdowns(prev => ({ ...prev, [settlement.id]: !prev[settlement.id] }))}
                        >
                          <DropdownToggle className="btn btn-sm btn-outline-secondary">
                            <FaEllipsisV style={{ color: 'white' }} />
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem>
                              <FaEye className="me-2" /> View Details
                            </DropdownItem>
                            {settlement.status.toLowerCase() === 'pending' && (
                              <DropdownItem 
                                onClick={() => {
                                  setSelectedSettlement(settlement);
                                  setPayoutModal(true);
                                }}
                              >
                                <FaCheck className="me-2" /> Process Payout
                              </DropdownItem>
                            )}
                            <DropdownItem>
                              <FaDownload className="me-2" /> Download Invoice
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>

        <div className="row align-items-center">
          <div className="col-md-6 d-flex">
            <div className="col-md-2">
              <Input
                type="select"
                id="paginate"
                onChange={(e) => setPaginate(e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </div>
          </div>
          <div className="col-md-6">
            <Pagination
              totalItems={settlementsCount}
              itemsPerPage={paginate}
              currentPage={page}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      {/* Payout Confirmation Modal */}
      <Modal isOpen={payoutModal} toggle={() => setPayoutModal(false)}>
        <ModalHeader toggle={() => setPayoutModal(false)}>
          Confirm Payout
        </ModalHeader>
        <ModalBody>
          {selectedSettlement && (
            <div>
              <p><strong>Partner:</strong> {selectedSettlement.partner_name}</p>
              <p><strong>Amount:</strong> ₹{parseFloat(selectedSettlement.net_payable).toLocaleString()}</p>
              <p><strong>Period:</strong> {new Date(selectedSettlement.period_start).toLocaleDateString()} - {new Date(selectedSettlement.period_end).toLocaleDateString()}</p>
              <p>Are you sure you want to process this payout?</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPayoutModal(false)}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={() => handlePayout(selectedSettlement?.id)}
          >
            Confirm Payout
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SettlementManagement;